import React, { useState, useRef, useLayoutEffect } from 'react';
import { WallConfig, CalculationResult } from '../types';

interface VisualizerProps {
  config: WallConfig;
  results: CalculationResult;
}

const Visualizer: React.FC<VisualizerProps> = ({ config, results }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const { width: containerW, height: containerH } = dimensions;
  const targetVal = results.targetDecimal;

  const ghostW = Math.max(results.totalWidth, results.totalHeight * targetVal);
  const ghostH = Math.max(results.totalHeight, results.totalWidth / targetVal);

  const padding = 40;
  const availableW = Math.max(0, containerW - padding * 2);
  const availableH = Math.max(0, containerH - padding * 2);

  const scale = (containerW && containerH) 
    ? Math.min(availableW / Math.max(results.totalWidth, ghostW), availableH / Math.max(results.totalHeight, ghostH))
    : 0.5;

  const wallStyle: React.CSSProperties = {
    width: `${results.totalWidth * scale}px`,
    height: `${results.totalHeight * scale}px`,
  };

  const fitStyle: React.CSSProperties = {
    width: `${results.fitMode.width * scale}px`,
    height: `${results.fitMode.height * scale}px`,
    left: `${results.fitMode.offsetX * scale}px`,
    top: `${results.fitMode.offsetY * scale}px`,
  };

  const ghostStyle: React.CSSProperties = {
    width: `${ghostW * scale}px`,
    height: `${ghostH * scale}px`,
    left: `calc(50% - ${(ghostW * scale) / 2}px)`,
    top: `calc(50% - ${(ghostH * scale) / 2}px)`,
  };

  const targetRatioStr = `${results.targetW}:${results.targetH}`;

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-video bg-slate-900/50 rounded-xl border border-slate-800 flex items-center justify-center overflow-hidden transition-all"
    >
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      {dimensions.width > 0 && (
        <div className="relative flex items-center justify-center w-full h-full">
          
          {/* Physical Wall - Furthest Back (z-0) */}
          <div 
            className="relative bg-yellow-500/10 shadow-[0_0_50px_rgba(0,0,0,0.3)] transition-all duration-500 ease-out z-0"
            style={wallStyle}
          >
            {/* Visual Border */}
            <div className="absolute inset-0 border border-yellow-500 pointer-events-none z-0 opacity-80" />

            {/* Fit Content Area - Middle (z-10 inside wall context) */}
            <div 
              className="absolute bg-transparent border-2 border-blue-500 transition-all duration-500 ease-out flex items-start justify-start overflow-hidden z-10 shadow-[inset_0_0_15px_rgba(59,130,246,0.1)]"
              style={fitStyle}
            >
              <div className="m-1.5 text-[8px] font-bold text-blue-400 uppercase select-none pointer-events-none whitespace-nowrap">
                Fit Content ({targetRatioStr})
              </div>
            </div>

            {/* Resolution Labels */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 font-mono-data text-[9px] text-slate-500 whitespace-nowrap">
              {results.totalWidth}px
            </div>
            <div className="absolute top-1/2 -right-12 -translate-y-1/2 font-mono-data text-[9px] text-slate-500 rotate-90 whitespace-nowrap">
              {results.totalHeight}px
            </div>
          </div>

          {/* Fill Content Frame - At the Front (z-20) */}
          <div 
            className="absolute border border-red-600/60 z-20 pointer-events-none transition-all duration-500 ease-out"
            style={ghostStyle}
          >
            <span className="absolute -top-4 left-0 text-[8px] text-red-500/80 uppercase font-bold tracking-widest whitespace-nowrap">
              Fill Content ({targetRatioStr})
            </span>
          </div>
        </div>
      )}

      <div className="absolute top-2 right-2 bg-slate-950/90 backdrop-blur px-2 py-1 rounded-full border border-slate-800 flex items-center gap-1.5 z-30 shadow-lg">
        <div className={`w-1 h-1 rounded-full animate-pulse ${results.accuracy > 95 ? 'bg-emerald-500' : results.accuracy > 80 ? 'bg-[#ff8000]' : 'bg-rose-500'}`}></div>
        <span className="text-[8px] font-mono-data font-bold uppercase tracking-wider text-slate-300">Live Simulation</span>
      </div>
    </div>
  );
};

export default Visualizer;